import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Box,
    Heading,
    HStack,
    Image,
    Text,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {GIFT_CARD_ALT} from '../../constants'

// Hooks
import {useConversionRate} from '@app/custom-contexts'
import useShipToCountry from '@app/hooks/use-ship-to-country'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useIntl} from 'react-intl'

// Utils
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getConvertedCurrencyCode, getConvertedPrice, formatPrice} from '@app/utils/price-utils'

/**
 * Component representing a gift certificate item.
 * Can be appended children.
 *
 * @param {JSX.Element} props.children - The children to be appended to the gift certificate item.
 * @param {Object} props.giftCertificateItem - The gift certificate item to be displayed.
 * @param {Object} props.imageStyles - The styles object for the image.
 * @param {Object} props.headingStyles - The styles object for the heading.
 *
 * @returns {JSX.Element} - The rendered gift certificate item component.
 */
const GiftCertificateItem = ({
    children,
    giftCertificateItem,
    conversionsRate,
    shipToCountryCode,
    imageStyles,
    headingStyles,
    ...props
}) => {
    const intl = useIntl()
    const contextConversionsRate = useConversionRate()
    const {shipToCountry: contextShipToCountry} = useShipToCountry()
    const {data: basket} = useCurrentBasket()
    const {currency: activeCurrency} = useCurrency()

    const priceConversionsRate = conversionsRate || contextConversionsRate
    const shipToCountryId = shipToCountryCode || contextShipToCountry?.id
    const giftCertificateItemCurrency =
        giftCertificateItem.c_giftCertificateCurrencyCode || basket?.currency || activeCurrency

    return (
        <Box
            position="relative"
            data-testid="sf-cart-giftcertificateitem"
            {...props}
            role="group"
            aria-label={intl.formatMessage({
                defaultMessage: 'Gift Card',
                id: 'checkout_payment.heading.gift_card'
            })}
        >
            <HStack width="full" gap={{base: 16, md: 24}} alignItems="stretch">
                <Image
                    alignSelf="flex-start"
                    src={getAssetUrl('static/img/cart/gift-card.webp')}
                    alt={intl.formatMessage(GIFT_CARD_ALT)}
                    {...imageStyles}
                />

                <VStack flex={1} alignItems="flex-start">
                    <VStack flexGrow={1} gap={12} alignItems="flex-start">
                        <Heading as="h2" textTransform="uppercase" {...headingStyles}>
                            <FormattedMessage
                                defaultMessage="Online Gift Card"
                                id="giftcard.name"
                            />
                        </Heading>
                        <Box>
                            <Text as="span" variant="bodyBase2" color="darkGray">
                                <FormattedMessage defaultMessage="Amount" id="giftcard.amount" />
                                :&nbsp;
                            </Text>
                            <Text as="span" variant="bodyBase2">
                                {priceConversionsRate &&
                                    formatPrice(
                                        getConvertedPrice(
                                            giftCertificateItem.amount,
                                            priceConversionsRate,
                                            true
                                        ),
                                        getConvertedCurrencyCode(
                                            priceConversionsRate,
                                            giftCertificateItemCurrency
                                        ),
                                        conversionsRate,
                                        shipToCountryId,
                                        intl
                                    )}
                            </Text>
                        </Box>
                    </VStack>

                    {children}
                </VStack>
            </HStack>
        </Box>
    )
}

GiftCertificateItem.propTypes = {
    giftCertificateItem: PropTypes.object.isRequired,
    conversionsRate: PropTypes.object,
    shipToCountryCode: PropTypes.string,
    imageStyles: PropTypes.object,
    headingStyles: PropTypes.object,
    children: PropTypes.node
}

export default GiftCertificateItem
